import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, Save, Edit2, X } from 'lucide-react';
import { api } from '../lib/api';
import type { System, Agent } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ExcelExportButton from '../components/ExcelExportButton';
import { useToast } from '../contexts/ToastContext';
import { isHouseForSystem } from '../lib/format';

export default function Agents() {
  const { sys: sysSlug } = useParams<{ sys: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['agents', sys],
    queryFn: () => api.listAgents(sys as System),
    enabled: !!sys,
  });

  // For the merge picker we want to see ALL agents in any system, not just
  // the ones with access to the currently-selected system. That way you can
  // link a Messer-only "Dylan Hernandez" with an Empire-ACA "Hernandez, Dylan".
  const allAgentsQ = useQuery({
    queryKey: ['agents-all'],
    queryFn: () => api.listAgents(),
  });

  if (!sys) return <div className="alert alert-warning"><p>Pick a system.</p></div>;
  if (q.isLoading) return <LoadingSpinner label="Loading agents…" />;
  const allAgents = (allAgentsQ.data?.agents ?? []).filter(
    (a) => a.agent_id !== 'unpaid' && a.agent_id !== 'unknown',
  );

  // Sort: agents paid in THIS system first, then house (not paid in this
  // system), alphabetical by display name within each group.
  const agents = (q.data?.agents ?? [])
    .filter((a) => a.agent_id !== 'unpaid' && a.agent_id !== 'unknown')
    .slice()
    .sort((a, b) => {
      const aHouse = isHouseForSystem(a, sys) ? 1 : 0;
      const bHouse = isHouseForSystem(b, sys) ? 1 : 0;
      if (aHouse !== bHouse) return aHouse - bHouse;
      return (a.display_name || '').localeCompare(b.display_name || '');
    });

  // Normalize display names so "Last, First" and "First Last" collapse to the
  // same key. That's how we flag cross-format duplicates.
  const normalizeName = (n: string) => {
    const trimmed = (n || '').trim().toLowerCase();
    if (trimmed.includes(',')) {
      const [last, first] = trimmed.split(',', 2).map((s) => s.trim());
      if (last && first) return `${first} ${last}`;
    }
    return trimmed;
  };

  // Bucket all known agents by normalized name so duplicates surface even if
  // they live in different systems.
  const dupesByName = new Map<string, Agent[]>();
  for (const a of allAgents) {
    const k = normalizeName(a.display_name);
    if (!k) continue;
    if (!dupesByName.has(k)) dupesByName.set(k, []);
    dupesByName.get(k)!.push(a);
  }
  const siblingsFor = (agent: Agent): Agent[] => {
    const k = normalizeName(agent.display_name);
    return (dupesByName.get(k) || []).filter((a) => a.agent_id !== agent.agent_id);
  };
  // Every other known agent — used as the merge-target list when you want
  // to link two records that don't share a normalized name yet.
  const otherAgentsFor = (agent: Agent): Agent[] =>
    allAgents.filter((a) => a.agent_id !== agent.agent_id);

  const handleSave = async (agentId: string, updates: Partial<Agent>) => {
    try {
      await api.updateAgent(agentId, updates);
      toast.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents-all'] });
      setEditingId(null);
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  };

  const handleMerge = async (fromAgentId: string, toAgentId: string, displayName: string) => {
    if (!confirm(
      `Merge "${displayName}" into the other "${displayName}"?\n\n` +
      `All rows, mappings, and payments from\n  ${fromAgentId}\nwill be transferred to\n  ${toAgentId}\n\n` +
      `Then ${fromAgentId} will be deleted. This is not reversible.`,
    )) return;
    try {
      const r = await api.mergeAgents(fromAgentId, toAgentId);
      toast.success(
        `Merged: ${r.rows_updated} rows, ${r.mappings_updated} mappings, ${r.payments_updated} payments moved`,
      );
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents-all'] });
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
    } catch (err) {
      toast.error(`Merge failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
            Agents — {sys}
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Auto-discovered from imports. Edit to set the agent's <strong>AgentID</strong> in your
            accounting tool — it's required for the CSV payment export to fill in.
          </p>
        </div>
        {agents.length > 0 && (
          <ExcelExportButton
            filename={`agents-${sys.toLowerCase()}.xlsx`}
            sheets={() => [
              {
                name: 'Agents',
                rows: [
                  ['Agent', 'Agent ID', 'Paid systems', 'Email', 'NPN (accounting)', 'System access', 'Aliases', 'Linked Cognito sub'],
                  ...agents.map((a) => [
                    a.display_name,
                    a.agent_id,
                    (a.paid_systems ?? []).join(', ') || '(house)',
                    a.email ?? '',
                    a.commission_system_agent_id ?? '',
                    (a.system_access ?? []).join(', '),
                    (a.aliases ?? []).join(', '),
                    a.cognito_sub ?? '',
                  ]),
                ],
              },
            ]}
          />
        )}
      </div>

      {agents.length === 0 ? (
        <div className="card text-center py-12">
          <Users size={32} className="mx-auto" style={{ color: 'var(--color-text-muted)' }} />
          <p className="mt-3" style={{ color: 'var(--color-text-muted)' }}>
            No agents yet — upload a commission file or seed Empire first.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Paid on {sys}?</th>
                <th>Email</th>
                <th>Accounting AgentID</th>
                <th>System access</th>
                <th>Aliases</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) =>
                editingId === a.agent_id ? (
                  <EditableRow
                    key={a.agent_id}
                    agent={a}
                    sys={sys}
                    onSave={handleSave}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <ReadOnlyRow
                    key={a.agent_id}
                    agent={a}
                    sys={sys}
                    siblings={siblingsFor(a)}
                    otherAgents={otherAgentsFor(a)}
                    onEdit={() => setEditingId(a.agent_id)}
                    onMerge={(toId) => handleMerge(a.agent_id, toId, a.display_name)}
                  />
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReadOnlyRow({
  agent: a,
  sys,
  siblings,
  otherAgents,
  onEdit,
  onMerge,
}: {
  agent: Agent;
  sys: System;
  siblings: Agent[];
  otherAgents: Agent[];
  onEdit: () => void;
  onMerge: (targetAgentId: string) => void;
}) {
  const paidHere = !isHouseForSystem(a, sys); // paid in THIS system?
  const otherSystems = (a.paid_systems ?? []).filter((s) => s !== sys);
  return (
    <tr>
      <td className="font-medium">
        <div className="flex items-center gap-2">
          <span>{a.display_name}</span>
          {a.requires_writing_agent_match && (
            <span
              className="badge badge-warning text-xs"
              title="Only paid on policies written under this agent's own NPN."
            >
              own-NPN only
            </span>
          )}
        </div>
        <div className="text-xs font-mono mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
          {a.agent_id}
        </div>
      </td>
      <td>
        {paidHere ? (
          <span className="badge badge-success text-xs">Yes</span>
        ) : (
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            House
          </span>
        )}
        {otherSystems.length > 0 && (
          <span
            className="text-xs ml-1"
            style={{ color: 'var(--color-text-muted)' }}
            title="Paid in another system"
          >
            (paid: {otherSystems.join(', ')})
          </span>
        )}
      </td>
      <td className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
        {a.email ?? '—'}
      </td>
      <td className="font-mono text-sm">
        {a.commission_system_agent_id ? (
          a.commission_system_agent_id
        ) : (
          <span className="badge badge-warning text-xs">missing</span>
        )}
      </td>
      <td>
        {a.system_access?.map((s) => (
          <span key={s} className="badge badge-primary mr-1 text-xs">
            {s}
          </span>
        ))}
      </td>
      <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        {a.aliases?.join(', ') || '—'}
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          {otherAgents.length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  onMerge(v);
                  e.target.value = '';
                }
              }}
              className="input text-xs py-1 pr-6"
              title="Merge this agent into another. Likely matches (same normalized name) appear first."
              style={{ minWidth: 220 }}
            >
              <option value="">Merge into…</option>
              {siblings.length > 0 && (
                <optgroup label="Likely match">
                  {siblings.map((s) => (
                    <option key={s.agent_id} value={s.agent_id}>
                      {s.display_name}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Other agents">
                {otherAgents
                  .filter((o) => !siblings.some((s) => s.agent_id === o.agent_id))
                  .sort((x, y) => x.display_name.localeCompare(y.display_name))
                  .map((o) => (
                    <option key={o.agent_id} value={o.agent_id}>
                      {o.display_name}
                    </option>
                  ))}
              </optgroup>
            </select>
          )}
          <button onClick={onEdit} className="btn-icon" aria-label="Edit">
            <Edit2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function EditableRow({
  agent: a,
  sys,
  onSave,
  onCancel,
}: {
  agent: Agent;
  sys: System;
  onSave: (agentId: string, updates: Partial<Agent>) => void;
  onCancel: () => void;
}) {
  const [displayName, setDisplayName] = useState(a.display_name);
  const [email, setEmail] = useState(a.email ?? '');
  const [externalId, setExternalId] = useState(a.commission_system_agent_id ?? '');
  const [aliases, setAliases] = useState(a.aliases?.join(', ') ?? '');
  const [paidHere, setPaidHere] = useState(!isHouseForSystem(a, sys));

  return (
    <tr style={{ backgroundColor: 'rgba(34, 211, 238, 0.06)' }}>
      <td>
        <input
          type="text"
          className="input text-sm font-medium"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Display name"
        />
      </td>
      <td>
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text)' }}>
          <input
            type="checkbox"
            checked={paidHere}
            onChange={(e) => setPaidHere(e.target.checked)}
          />
          {paidHere ? `Paid on ${sys}` : 'House'}
        </label>
      </td>
      <td>
        <input
          type="email"
          className="input text-sm"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="agent@example.com"
        />
      </td>
      <td>
        <input
          type="text"
          className="input text-sm font-mono"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          placeholder="e.g. 19853042"
        />
      </td>
      <td>
        {a.system_access?.map((s) => (
          <span key={s} className="badge badge-primary mr-1 text-xs">
            {s}
          </span>
        ))}
      </td>
      <td>
        <input
          type="text"
          className="input text-sm"
          value={aliases}
          onChange={(e) => setAliases(e.target.value)}
          placeholder="comma-separated"
        />
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() =>
              onSave(a.agent_id, {
                display_name: displayName.trim() || a.display_name,
                email: email.trim() || null,
                commission_system_agent_id: externalId.trim(),
                aliases: aliases
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
                // Toggle THIS system in paid_systems; preserve the others.
                paid_systems: paidHere
                  ? Array.from(new Set([...(a.paid_systems ?? []), sys]))
                  : (a.paid_systems ?? []).filter((s) => s !== sys),
              })
            }
            className="btn btn-primary py-1 px-2 text-xs flex items-center gap-1"
          >
            <Save size={12} /> Save
          </button>
          <button onClick={onCancel} className="btn-icon" aria-label="Cancel">
            <X size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}
